import type { KdsDevice, UpdateKdsDeviceRequest } from '../../queries/kdsTypes';

/**
 * Logika przypisania ekranu KDS do punktu i stanowiska, bez Reacta (osobny plik, żeby
 * Fast Refresh nie przeładowywał komponentu i żeby dało się ją testować).
 */
export interface KdsAssignmentValue {
  /** '' = wszystkie punkty. */
  outletId: string;
  /** '' = wszystkie stanowiska. */
  stationId: string;
}

export interface KdsAssignmentPayload {
  roomServiceId: string | null;
  gastronomyPointId: string | null;
}

/**
 * Punkt, w którym ekran szuka stanowisk. Przy jednym punkcie w hotelu pole punktu jest
 * ukryte, więc stanowiska bierzemy z tego jedynego punktu.
 */
export const resolveKdsOutletId = (
  outletId: string,
  roomServices: { id: string }[],
): string => outletId || (roomServices.length === 1 ? roomServices[0].id : '');

/**
 * Wartości dla backendu. Przy jednym punkcie pole punktu jest ukryte, więc wybrane
 * stanowisko niesie ze sobą ten jedyny punkt (backend przyjmuje stanowisko tylko z punktem).
 */
export const toKdsAssignmentPayload = (
  value: KdsAssignmentValue,
  roomServices: { id: string }[],
): KdsAssignmentPayload => ({
  roomServiceId: value.outletId || (value.stationId ? resolveKdsOutletId('', roomServices) : '') || null,
  gastronomyPointId: value.stationId || null,
});

/** Zmiana punktu zawsze wraca do „Wszystkie stanowiska” (stanowiska należą do punktu). */
export const changeKdsOutlet = (value: KdsAssignmentValue, outletId: string): KdsAssignmentValue => (
  outletId === value.outletId ? value : { outletId, stationId: '' }
);

/** Ekran ma przypisane stanowisko, którego już nie ma w room service. */
export const hasDeletedKdsStation = (device: Pick<KdsDevice, 'gastronomyPointId' | 'gastronomyPoint'>): boolean => (
  !!device.gastronomyPointId && !device.gastronomyPoint
);

/** Stan formularza edycji; usuniętego stanowiska nie da się wybrać, więc startujemy od „Wszystkie”. */
export const initialKdsAssignment = (
  device: Pick<KdsDevice, 'roomServiceId' | 'gastronomyPointId' | 'gastronomyPoint'>,
): KdsAssignmentValue => ({
  outletId: device.roomServiceId ?? '',
  stationId: device.gastronomyPoint ? device.gastronomyPointId ?? '' : '',
});

/**
 * Żądanie `v3-updateKdsDevice` tylko ze zmienionymi polami (pominięte = bez zmian,
 * null = wyczyść). Zwraca null, gdy nic się nie zmieniło.
 */
export const buildKdsUpdateRequest = (
  hotelId: string,
  device: Pick<KdsDevice, 'deviceId' | 'roomServiceId' | 'gastronomyPointId'>,
  value: KdsAssignmentValue,
  roomServices: { id: string }[],
): UpdateKdsDeviceRequest | null => {
  const payload = toKdsAssignmentPayload(value, roomServices);
  const request: UpdateKdsDeviceRequest = { hotelId, deviceId: device.deviceId };
  if (payload.roomServiceId !== (device.roomServiceId ?? null)) request.roomServiceId = payload.roomServiceId;
  // Usunięte stanowisko też jest „inne” niż null, więc trafia tu jako jawne wyczyszczenie.
  if (payload.gastronomyPointId !== (device.gastronomyPointId ?? null)) {
    request.gastronomyPointId = payload.gastronomyPointId;
  }
  return 'roomServiceId' in request || 'gastronomyPointId' in request ? request : null;
};
